import LegalPageShell from "@/components/site/LegalPageShell";
import { useLanguage, pick } from "@/lib/i18n";
import { SUPPORT_EMAIL } from "@/lib/translations";

const COPY = {
    en: {
        title: "Child Safety Policy",
        subtitle: "How Dino Detective keeps children safe in line with the Google Play Families program.",
        sections: [
            { id: "designed", label: "Designed for families" },
            { id: "no-ugc", label: "No user-generated content" },
            { id: "no-chat", label: "No chat functionality" },
            { id: "no-social", label: "No social networking" },
            { id: "no-ads", label: "No third-party advertising" },
            { id: "no-comms", label: "No direct child communication" },
            { id: "parent-area", label: "Parent Area (PIN)" },
            { id: "safe-content", label: "Safe educational content" },
            { id: "reporting", label: "Reporting concerns" },
        ],
    },
    de: {
        title: "Kinderschutz-Richtlinie",
        subtitle: "Wie Dino Detective Kinder sicher hält – im Einklang mit dem Google Play Families-Programm.",
        sections: [
            { id: "designed", label: "Für Familien entwickelt" },
            { id: "no-ugc", label: "Keine nutzergenerierten Inhalte" },
            { id: "no-chat", label: "Keine Chat-Funktion" },
            { id: "no-social", label: "Keine sozialen Netzwerke" },
            { id: "no-ads", label: "Keine Drittanbieter-Werbung" },
            { id: "no-comms", label: "Keine direkte Kontaktaufnahme zu Kindern" },
            { id: "parent-area", label: "Elternbereich (PIN)" },
            { id: "safe-content", label: "Sichere Lerninhalte" },
            { id: "reporting", label: "Bedenken melden" },
        ],
    },
};

export default function ChildSafety() {
    const { lang } = useLanguage();
    const t = pick(COPY, lang);
    return (
        <LegalPageShell title={t.title} subtitle={t.subtitle} sections={t.sections}>
            {lang === "en" ? <EN /> : <DE />}
        </LegalPageShell>
    );
}

function EN() {
    return (
        <>
            <h2 id="designed">Designed for children and families</h2>
            <p>
                Dino Detective is built from the ground up for children and families. Every design decision — from content, to monetisation, to
                technical permissions — is made with child safety as a primary concern. The app participates in Google Play’s Designed for
                Families / Teacher Approved guidelines and follows the principles of the EU GDPR (including Article 8) and, where applicable,
                the U.S. COPPA framework.
            </p>

            <h2 id="no-ugc">No user-generated content</h2>
            <p>
                The app does not allow children (or anyone) to post, upload or share photos, videos, audio, text or files. All content shown in
                the game is curated by us. There are no comment sections, no forums and no public profiles.
            </p>

            <h2 id="no-chat">No chat functionality</h2>
            <p>
                Dino Detective contains <strong>no chat or messaging features of any kind</strong>. Children cannot send or receive messages to
                or from any other user inside the app.
            </p>

            <h2 id="no-social">No social networking</h2>
            <p>
                There are no friends lists, follower counts, leaderboards with usernames, or social network logins. Progress is private and
                stored locally on the device.
            </p>

            <h2 id="no-ads">No third-party advertising</h2>
            <p>
                The app contains no third-party advertising, no advertising SDKs, no ad networks and no in-app ads of any kind. No advertising
                identifiers are collected and no advertising profiles are built. The only purchase in the app is the optional one-time premium
                upgrade through Google Play Billing.
            </p>

            <h2 id="no-comms">No direct child communication</h2>
            <p>
                The app never asks a child for their name, email, phone number, address or any other contact information for the purpose of
                contacting them. All support communication takes place through a parent or guardian via{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>

            <h2 id="parent-area">Parent Area protected by PIN</h2>
            <p>
                Settings, purchase actions and any links that lead outside the app (including app store pages, social links and external
                websites) are placed inside a Parent Area protected by a PIN gate. The PIN gate uses an age-appropriate challenge (for example,
                a written-out number or simple math question) to make it difficult for young children to bypass.
            </p>

            <h2 id="safe-content">Safe educational content</h2>
            <ul>
                <li>No violence, no scary imagery, no inappropriate themes.</li>
                <li>Friendly, encouraging tone and age-appropriate language.</li>
                <li>Educational content focused on math skills, logical thinking and curiosity about dinosaurs.</li>
                <li>All puzzles, illustrations and rewards are designed and reviewed for a young audience.</li>
            </ul>

            <h2 id="reporting">Reporting concerns</h2>
            <p>
                If you are a parent, guardian, teacher or any concerned adult and you notice content, behaviour or a bug that you believe could
                affect a child’s safety, please contact us immediately at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We take such
                reports seriously and aim to respond within 48 hours.
            </p>
        </>
    );
}

function DE() {
    return (
        <>
            <h2 id="designed">Für Kinder und Familien entwickelt</h2>
            <p>
                Dino Detective wurde von Grund auf für Kinder und Familien gestaltet. Jede Entscheidung – von Inhalten über Monetarisierung bis
                zu technischen Berechtigungen – berücksichtigt die Sicherheit von Kindern. Die App orientiert sich an den Richtlinien des
                Google Play „Designed for Families“-Programms, an der DSGVO (insbesondere Art. 8) und – wo anwendbar – an COPPA.
            </p>

            <h2 id="no-ugc">Keine nutzergenerierten Inhalte</h2>
            <p>
                Kinder (und andere Nutzer) können in der App keine Fotos, Videos, Audios, Texte oder Dateien hochladen oder teilen. Alle Inhalte
                werden von uns kuratiert. Es gibt keine Kommentare, Foren oder öffentlichen Profile.
            </p>

            <h2 id="no-chat">Keine Chat-Funktion</h2>
            <p>
                Dino Detective enthält <strong>keinerlei Chat- oder Messaging-Funktionen</strong>. Kinder können innerhalb der App keine
                Nachrichten an andere Nutzer senden oder empfangen.
            </p>

            <h2 id="no-social">Keine sozialen Netzwerke</h2>
            <p>
                Es gibt keine Freundeslisten, keine Follower, keine öffentlichen Bestenlisten und keine Social-Logins. Der Fortschritt wird privat
                auf dem Gerät gespeichert.
            </p>

            <h2 id="no-ads">Keine Drittanbieter-Werbung</h2>
            <p>
                Die App enthält keine Drittanbieter-Werbung, keine Werbe-SDKs, keine Werbenetzwerke und keine In-App-Werbung. Es werden keine
                Werbe-IDs gesammelt und keine Werbeprofile erstellt. Der einzige In-App-Kauf ist das optionale, einmalige Premium-Upgrade über
                Google Play Billing.
            </p>

            <h2 id="no-comms">Keine direkte Kontaktaufnahme zu Kindern</h2>
            <p>
                Die App fragt Kinder niemals nach Name, E-Mail, Telefonnummer oder Adresse, um mit ihnen Kontakt aufzunehmen. Die gesamte
                Support-Kommunikation läuft über Eltern oder Erziehungsberechtigte via{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>

            <h2 id="parent-area">Elternbereich mit PIN</h2>
            <p>
                Einstellungen, Kaufaktionen und Links, die aus der App hinausführen (z. B. App-Store, soziale Medien, externe Webseiten), sind
                durch einen Elternbereich mit PIN geschützt. Die Abfrage erfolgt altersgerecht (z. B. ausgeschriebene Zahl oder einfache
                Mathe-Aufgabe), damit kleine Kinder sie nicht versehentlich überwinden.
            </p>

            <h2 id="safe-content">Sichere Lerninhalte</h2>
            <ul>
                <li>Keine Gewalt, keine gruseligen Bilder, keine unangemessenen Themen.</li>
                <li>Freundlicher, ermutigender Tonfall und altersgerechte Sprache.</li>
                <li>Lerninhalte zu Mathematik, logischem Denken und Wissen über Dinosaurier.</li>
                <li>Alle Rätsel, Illustrationen und Belohnungen werden für ein junges Publikum geprüft.</li>
            </ul>

            <h2 id="reporting">Bedenken melden</h2>
            <p>
                Wenn du als Elternteil, Lehrkraft oder besorgte Person Inhalte, Verhalten oder Fehler bemerkst, die die Sicherheit eines Kindes
                beeinträchtigen könnten, kontaktiere uns bitte umgehend unter <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Wir nehmen
                solche Meldungen ernst und antworten in der Regel innerhalb von 48 Stunden.
            </p>
        </>
    );
}
